import React from 'react';
import {
  Alert, Badge, Button, ConfirmDialog, ErrorState, PageHeader, SkeletonText,
  TextInput, useToast, useWhatsappPairing,
} from '@parsina/framework';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Smartphone } from 'lucide-react';

import { useTranslation, toAsciiDigits } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { ADMIN_BFF_URL } from '../../lib/runtimeEnv';
import useDocumentTitle from '../../lib/useDocumentTitle';
import {
  PAIRING_PATH, SEND_TEST, SESSION_QUERY, UNLINK,
} from './queries';

/**
 * «واتس‌اپ پارسینا» — the one screen where the company's own WhatsApp number is
 * linked, watched and unlinked.
 *
 * WHY IT IS IN THIS PANEL AND NOT THE CLINIC'S. A clinic pairs its own number in
 * «کلینیک من › واتس‌اپ»; this is Parsina's, and it belongs to nobody's tenant.
 * The admin panel already gates on `platform_admin` and already holds the other
 * things that are the platform's rather than a customer's — AI pricing, the
 * marketplace commission. The BFF re-checks the role; this screen being behind
 * `RequireAccess` is convenience, not the gate.
 *
 * THE MECHANICS ARE THE KIT'S. `useWhatsappPairing` reads the BFF's SSE stream,
 * and the part it exists for is cancellation: the gateway opens a FRESH WhatsApp
 * device for every attempt and holds it for three minutes, so a stream nobody
 * cancelled is an abandoned device in a real person's «linked devices» list.
 * This screen supplies only its own transport and its own chrome.
 *
 * THERE ARE TWO WAYS IN, AND THE SECOND IS THE ONE PEOPLE USE. A QR needs the
 * phone and the screen in the same pair of hands. An operator with the company
 * phone in a drawer two rooms away types an eight-character code instead, so the
 * code is a peer of the QR here, not a hidden fallback.
 */
export default function Whatsapp() {
  useDocumentTitle('nav.title.whatsapp');
  const { t, formatDate, formatNumber } = useTranslation();
  const toast = useToast();

  const [method, setMethod] = React.useState('QR');
  const [phone, setPhone] = React.useState('');
  const [startError, setStartError] = React.useState(null);
  const [confirmUnlink, setConfirmUnlink] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const loadSession = React.useCallback(
    async () => (await gql(SESSION_QUERY))?.platformWhatsappSession || null,
    [],
  );
  const unlinkSession = React.useCallback(async () => { await gql(UNLINK); }, []);

  const {
    session, loading, error, pairing, refetch, startLink, stopPairing, unlink,
  } = useWhatsappPairing({
    pairingUrl: `${ADMIN_BFF_URL}${PAIRING_PATH}`,
    getToken,
    loadSession,
    unlinkSession,
  });

  const configured = session?.configured !== false;
  const state = session?.state || 'UNSPECIFIED';
  const linked = state === 'CONNECTED' || state === 'DISCONNECTED';

  const begin = async (nextMethod) => {
    setStartError(null);
    const typed = toAsciiDigits(phone).replace(/[^\d+]/g, '');
    if (nextMethod === 'PAIRING_CODE' && !typed) {
      setStartError(t('whatsapp.link.phoneRequired'));
      return;
    }
    setMethod(nextMethod);
    try {
      await startLink(nextMethod, nextMethod === 'PAIRING_CODE' ? typed : '');
    } catch (err) {
      // `serverMessage` ONLY, never `err.message`. The BFF's sentence when it has
      // one — it knows whether this was a missing permission or an unreachable
      // gateway, and the screen does not — and our own catalogue otherwise.
      // Rendering `err.message` is how the browser's «signal is aborted without
      // reason» once reached an operator on a Persian page (kit v0.18.14).
      setStartError(err?.serverMessage || t('whatsapp.link.failed'));
    }
  };

  const doUnlink = async () => {
    setConfirmUnlink(false);
    setBusy(true);
    try {
      await unlink();
      toast.success(t('whatsapp.unlink.done'));
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="wa-screen">
      <PageHeader title={t('whatsapp.title')} sub={t('whatsapp.subtitle')} />

      {!configured && (
        <Alert variant="warning" data-testid="wa-not-configured">
          <strong className="block">{t('whatsapp.notConfigured.title')}</strong>
          {t('whatsapp.notConfigured.body')}
        </Alert>
      )}

      {loading && <SkeletonText lines={4} />}

      {!loading && error && !session && (
        <ErrorState
          title={t('whatsapp.errors.load')}
          description={errorMessage(error, t)}
          onRetry={refetch}
          retryLabel={t('common.actions.retry')}
          data-testid="wa-error"
        />
      )}

      {!loading && session && (
        <section
          className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4"
          data-testid="wa-card"
        >
          <header className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Badge variant={badgeVariant(state)} data-testid="wa-state">
                {t(`whatsapp.state.${state}`)}
              </Badge>
              <span className="text-xs text-muted-foreground" dir="ltr">
                {t('whatsapp.account', { account: session.accountId })}
              </span>
            </div>
            {linked && (
              <Button
                variant="ghost"
                onClick={() => setConfirmUnlink(true)}
                disabled={busy}
                data-testid="wa-unlink"
              >
                {t('whatsapp.unlink.action')}
              </Button>
            )}
          </header>

          {/* A state the operator must act on says so, in words. `detail` is the
              gateway's own reason when it has one and is better than ours. */}
          {(state === 'DISCONNECTED' || state === 'LOGGED_OUT' || state === 'UNSPECIFIED') && (
            <Alert variant={state === 'DISCONNECTED' ? 'info' : 'warning'} data-testid="wa-hint">
              {session.detail || t(`whatsapp.hint.${state}`)}
            </Alert>
          )}
          {session.sendDisabled && (
            <Alert variant="warning" data-testid="wa-send-disabled">
              {t('whatsapp.session.sendDisabled')}
            </Alert>
          )}

          {linked && (
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2" data-testid="wa-facts">
              <Fact label={t('whatsapp.session.phone')} value={session.phone} ltr />
              <Fact label={t('whatsapp.session.device')} value={session.deviceLabel} />
              <Fact
                label={t('whatsapp.session.linkedAt')}
                value={session.linkedAt ? formatDate(session.linkedAt) : null}
              />
              <Fact
                label={t('whatsapp.session.hourly')}
                value={t('whatsapp.session.hourlyValue', {
                  sent: formatNumber(session.sentLastHour || 0),
                  max: formatNumber(session.maxPerHour || 0),
                })}
              />
            </dl>
          )}

          {configured && (
            <LinkPanel
              t={t}
              state={state}
              pairing={pairing}
              method={method}
              phone={phone}
              onPhone={setPhone}
              onBegin={begin}
              onStop={() => { stopPairing(); setStartError(null); }}
              error={startError || (error ? errorMessage(error, t) : null)}
            />
          )}
        </section>
      )}

      {linked && <TestSend t={t} toast={toast} />}

      <ConfirmDialog
        open={confirmUnlink}
        onOpenChange={setConfirmUnlink}
        title={t('whatsapp.unlink.confirmTitle')}
        description={t('whatsapp.unlink.confirmBody')}
        confirmLabel={t('whatsapp.unlink.action')}
        cancelLabel={t('common.actions.cancel')}
        onConfirm={doUnlink}
        destructive
      />
    </div>
  );
}

function Fact({ label, value, ltr }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground" dir={ltr ? 'ltr' : undefined}>
        {value || '—'}
      </dd>
    </div>
  );
}

/**
 * Starting, showing and abandoning a link attempt.
 *
 * `CONNECTING` is its own state and not a spinner on the button: the first code
 * can take a second or two to arrive from WhatsApp, and a button that merely
 * looks busy invites a second press — which aborts the first attempt and opens
 * yet another device.
 */
function LinkPanel({ t, state, pairing, method, phone, onPhone, onBegin, onStop, error }) {
  const live = !!pairing;
  const showQr = pairing?.kind === 'QR' && pairing.code;
  const showCode = pairing?.kind === 'PAIRING_CODE' && pairing.code;

  return (
    <div className="flex flex-col gap-3" data-testid="wa-link">
      {error && <Alert variant="error" data-testid="wa-link-error">{error}</Alert>}

      {!live && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => onBegin('QR')} data-testid="wa-start-qr">
              <QrCode className="size-4" aria-hidden />
              {state === 'UNLINKED' ? t('whatsapp.link.start') : t('whatsapp.link.relink')}
              {' · '}
              {t('whatsapp.link.qrTab')}
            </Button>
            <Button variant="secondary" onClick={() => onBegin('PAIRING_CODE')} data-testid="wa-start-code">
              <Smartphone className="size-4" aria-hidden />
              {t('whatsapp.link.codeTab')}
            </Button>
          </div>
          {/* The number is only needed for the typed code, but the field is
              always visible: hiding it behind the button would mean pressing
              «کد ۸ رقمی», being refused, typing, and pressing again. */}
          <TextInput
            label={t('whatsapp.link.phoneLabel')}
            placeholder={t('whatsapp.link.phonePlaceholder')}
            value={phone}
            onChange={(e) => onPhone(e.target.value)}
            dir="ltr"
            data-testid="wa-phone"
          />
        </div>
      )}

      {live && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-border p-4">
          {pairing.kind === 'CONNECTING' && (
            <p className="text-sm text-muted-foreground" data-testid="wa-connecting">
              {t('whatsapp.link.connecting')}
            </p>
          )}

          {showQr && (
            <>
              {/* White ground and a quiet zone regardless of theme: a dark-mode
                  QR on a dark card is a QR no camera reads. */}
              <div className="rounded-md bg-white p-3" data-testid="wa-qr">
                <QRCodeSVG value={pairing.code} size={220} includeMargin={false} />
              </div>
              <p className="text-center text-xs text-muted-foreground">{t('whatsapp.link.qrHint')}</p>
            </>
          )}

          {showCode && (
            <>
              <p
                className="select-all font-mono text-3xl font-bold tracking-[0.25em] text-foreground"
                dir="ltr"
                data-testid="wa-code"
              >
                {pairing.code}
              </p>
              <p className="text-center text-xs text-muted-foreground">{t('whatsapp.link.codeHint')}</p>
            </>
          )}

          {pairing.kind === 'TIMEOUT' && (
            <p className="text-sm text-foreground" data-testid="wa-timeout">
              {pairing.message || t('whatsapp.link.timeout')}
            </p>
          )}

          {pairing.expiresAt && <Countdown t={t} expiresAt={pairing.expiresAt} />}

          <Button variant="ghost" onClick={onStop} data-testid="wa-cancel">
            {t('whatsapp.link.cancel')}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Seconds left on the code on screen.
 *
 * Counts down from an ABSOLUTE instant the hook stamped, never from a duration:
 * a component that re-renders for its own reasons must not restart the clock and
 * show an expired code as live.
 */
export function Countdown({ t, expiresAt }) {
  const left = () => Math.max(0, Math.round((Date.parse(expiresAt) - Date.now()) / 1000));
  const [seconds, setSeconds] = React.useState(left);

  React.useEffect(() => {
    setSeconds(left());
    const timer = setInterval(() => setSeconds(left()), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt]);

  return (
    <p className="text-xs text-muted-foreground" data-testid="wa-countdown">
      {seconds > 0 ? t('whatsapp.link.expiresIn', { seconds }) : t('whatsapp.link.expired')}
    </p>
  );
}

/**
 * One message to a number the operator types.
 *
 * This is how the link is proved. Without it the first real message this number
 * ever sends goes to a doctor who just signed up, and an operator has no way to
 * find out beforehand that the session is linked but the number is, say, banned.
 */
function TestSend({ t, toast }) {
  const [phone, setPhone] = React.useState('');
  const [pending, setPending] = React.useState(false);

  const send = async () => {
    const typed = toAsciiDigits(phone).replace(/[^\d+]/g, '');
    if (!typed || pending) return;
    setPending(true);
    try {
      const data = await gql(SEND_TEST, { phone: typed });
      const res = data?.sendPlatformWhatsappTest;
      if (res?.sent) toast.success(t('whatsapp.test.sent'));
      else toast.error(res?.detail || t('whatsapp.link.failed'));
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setPending(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-testid="wa-test"
    >
      <div>
        <h2 className="text-sm font-bold text-foreground">{t('whatsapp.test.title')}</h2>
        <p className="mt-1 text-xs text-muted-foreground">{t('whatsapp.test.body')}</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1">
          <TextInput
            label={t('whatsapp.test.phoneLabel')}
            placeholder={t('whatsapp.link.phonePlaceholder')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            dir="ltr"
            data-testid="wa-test-phone"
          />
        </div>
        <Button onClick={send} disabled={pending || !phone.trim()} data-testid="wa-test-send">
          {t('whatsapp.test.send')}
        </Button>
      </div>
    </section>
  );
}

function badgeVariant(state) {
  if (state === 'CONNECTED') return 'success';
  if (state === 'DISCONNECTED') return 'warning';
  if (state === 'LOGGED_OUT') return 'destructive';
  return 'outline';
}
